import { action, computed, onBecomeObserved } from 'mobx';
import api from 'core/util/api';
import $auth from 'app/stores/$auth';
import { RBACAssetTagPermission } from '@kentik/ui-shared/rbac/constants';
import ActiveDeviceLabelCollection from './ActiveDeviceLabelCollection';
import LabelCollection from './LabelCollection';
import UserPermGroupModel from './UserPermGroupModel';

export class LabelStore {
  labels = new LabelCollection();

  userPermGroup = new UserPermGroupModel();

  activeDeviceLabels = new ActiveDeviceLabelCollection();

  constructor(options = {}) {
    Object.assign(this, options);

    // Load lazily the first time anything observes the options, so this doesn't add to app boot
    onBecomeObserved(this, 'activeDeviceLabelOptions', () => this.activeDeviceLabels.fetch().catch(() => {}));
  }

  initialize() {
    this.labels.fetch();
    if (!$auth.isSubtenantUser && !$auth.isSharedUser) {
      this.userPermGroup.fetch();
    }
  }

  @computed
  get canCreate() {
    return this.store.$auth.hasRbacPermissions([RBACAssetTagPermission.CREATE]);
  }

  @computed
  get canView() {
    return this.store.$auth.hasRbacPermissions([RBACAssetTagPermission.READ]);
  }

  @computed
  get canUpdate() {
    return this.store.$auth.hasRbacPermissions([RBACAssetTagPermission.UPDATE]);
  }

  @computed
  get canDelete() {
    return this.store.$auth.hasRbacPermissions([RBACAssetTagPermission.DELETE]);
  }

  @computed
  get activeDeviceLabelOptions() {
    return this.getLabelOptions(this.activeDeviceLabels.models);
  }

  /**
   * Re-fetch active device labels after a change, but only if they were ever requested.
   * If nothing has used them yet, the first use will load current data anyway.
   * @returns {Promise<void>}
   */
  refreshActiveDeviceLabels() {
    if (!this.activeDeviceLabels.hasFetched) {
      return Promise.resolve();
    }

    return this.activeDeviceLabels.fetch({ force: true }).catch((err) => {
      console.error('Error fetching active device labels', err);
    });
  }

  /**
   * Update label mappings table for type, items and label_id (optional) provided.
   * @param {String} type 'synth_agent', 'synth_test', 'device', etc.
   * @param {String} method 'INSERT' or 'DELETE' (by this.id)
   * @param {Array} items Depending on type, an arr of agent_id, test_id, etc.
   * @param {Array} [labels] Optional array of label_ids used for INSERT statements
   */
  @action
  updateItems(type, method, items, labels) {
    const data = { type, method, items, labels };
    this.labels.setRequestStatus('fetching');
    return api
      .post('/api/ui/labels/items', { data })
      .then((resp) => this.labels.processData(resp))
      .then((res) => {
        this.labels.setLastUpdated();
        this.labels.setRequestStatus(null);

        if (type === 'device') {
          this.refreshActiveDeviceLabels();
        }

        return res;
      });
  }

  /**
   * @param itemType i.e. 'synth_agent', 'synth_test, 'device', 'policy', 'dashboard', 'saved_view'
   * @param [itemId] optional; filter further by id
   * @returns {LabelModel[]}
   */
  getLabels(itemType, itemId) {
    return this.labels.get().filter((m) => {
      const items = m.get('items');
      return !!items.find((i) => {
        if (itemId) {
          return `${i.item_id}` === `${itemId}` && i.item_type === itemType;
        }
        return i.item_type === itemType;
      });
    });
  }

  /**
   * @param {number[]} labelIds
   * @returns {number[]}
   */
  getLabelsById(labelIds) {
    return this.labels.get().filter((m) => labelIds.includes(m.id));
  }

  @computed
  get options() {
    return this.getLabelOptions(this.companyLabels.models);
  }

  @computed
  get companyLabels() {
    return new LabelCollection(
      this.labels.get().filter((label) => label.get('company_id') === $auth.getActiveUserProperty('company_id'))
    );
  }

  // Cache for computed values by type
  companyLabelsByTypeCache = new Map();

  getCompanyLabelsByType = (type) => {
    if (!this.companyLabelsByTypeCache.has(type)) {
      // Create a new computed value for this type
      this.companyLabelsByTypeCache.set(
        type,
        computed(() => {
          const labelIDs = this.userPermGroup.get(type);
          return labelIDs ? this.getLabelsById(labelIDs) : this.companyLabels;
        })
      );
    }
    // Return the computed value for this type
    return this.companyLabelsByTypeCache.get(type).get();
  };

  getLabelOptions(labels, includeId = false) {
    const options = labels.map((label) => {
      const labelOption = label.option || {
        value: label.id,
        label: label.name,
        bg: label.color,
        color: label.color
      };

      // in case there's an option defined on the label, but we still want the id
      if (!labelOption.id && includeId) {
        labelOption.id = label.id;
      }
      labelOption.isPreset = label.isPreset;

      return labelOption;
    });

    return options.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
  }

  getLabelOptionsFromIds(labelIds) {
    return this.getLabelOptions(
      this.labels.get().filter((label) => labelIds.includes(label.id)),
      true
    );
  }

  getLabelsFromModelOrLocation(model, location) {
    return (model?.labels?.length > 0 ? model.labels : location?.state?.labels) || [];
  }

  labelMultiSelectModelFilter(labels, values, useAndLogic) {
    const conditionFn = useAndLogic ? 'every' : 'some';
    return values[conditionFn]((value) => {
      if (value === '-') {
        return labels.length === 0;
      }
      return labels.some((label) => label.id === value);
    });
  }
}

export default new LabelStore();
